/* Фид карточки: задачи и заметки двумя раздельными блоками + композер.
   ТЗ 6.5 (сделка) и 6.6 (проект). Удаление — свайпом строки влево. */

import { ddmm, toISO } from "@mera/shared";
import React, { useEffect, useState } from "react";

import { api } from "../lib/api.js";
import { useApp } from "../lib/store.jsx";
import { confirmDialog, haptic } from "../lib/tg.js";
import { Ion } from "./icons.jsx";
import SwipeRow from "./SwipeRow.jsx";
import { Empty, Eyebrow, Hint, RichText } from "./ui.jsx";

/**
 * @param scope   "deals" | "projects"
 * @param id      идентификатор карточки
 * @param items   элементы фида (задачи и заметки)
 * @param canEdit false — только чтение: без композера, свайпов и галочек
 */
export default function Feed({ scope, id, items, onChanged, today, canEdit = true }) {
  const { me, reloadAlerts } = useApp();
  const [text, setText] = useState("");
  const [kind, setKind] = useState("note"); // note | task
  /* Срок задачи по умолчанию — сегодня: пустое поле даты в Telegram выглядит
     как незаполненный прочерк, а ставят задачи чаще всего на сегодня */
  const [due, setDue] = useState(today || "");
  const [dueTime, setDueTime] = useState("12:00");
  const [remind, setRemind] = useState(false);
  const [who, setWho] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setWho(me?.name || "");
  }, [me]);

  const base = `/${scope}/${id}`;

  const send = async () => {
    if (!text.trim()) return;
    if (kind === "task" && !due) {
      haptic.warning();
      return;
    }
    setBusy(true);
    try {
      if (kind === "task") {
        await api.post(`${base}/tasks`, {
          title: text.trim(),
          who: who || me?.name,
          dueDate: due,
          dueTime,
          remind,
        });
      } else {
        await api.post(`${base}/notes`, { text: text.trim() });
      }
      setText("");
      setDue(today || "");
      setRemind(false);
      haptic.success();
      onChanged?.();
      reloadAlerts();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    const ok = await confirmDialog(
      it.itemType === "task" ? `Удалить задачу «${it.title}»?` : "Удалить заметку?"
    );
    if (!ok) return;
    await api.del(`${base}/feed/${it.id}`, { type: it.itemType });
    haptic.success();
    onChanged?.();
    reloadAlerts();
  };

  // Закрытие — одним тапом, без анкеты с «Итогом»
  const completeTask = async (task) => {
    await api.patch(`${base}/tasks/${task.id}`, { done: true });
    haptic.success();
    onChanged?.();
    reloadAlerts();
  };

  const reopenTask = async (task) => {
    await api.patch(`${base}/tasks/${task.id}`, { done: false });
    haptic.select();
    onChanged?.();
  };

  const tasks = (items || []).filter((it) => it.itemType === "task");
  const notes = (items || []).filter((it) => it.itemType !== "task");

  return (
    <>
      {/* Задачи и заметки — раздельными блоками; удаление свайпом строки влево */}
      <Eyebrow>Задачи</Eyebrow>
      <div className="px-4">
        <div className="card-tg py-1 overflow-hidden">
          {!tasks.length ? (
            <Empty text="Задач нет" icon={Ion.checkbox} />
          ) : (
            tasks.map((it, i) => (
              <SwipeRow
                key={it.id}
                onDelete={canEdit ? () => remove(it) : null}
                className={i > 0 ? "hairline-t" : ""}
              >
                <TaskItem
                  t={it}
                  today={today}
                  onDone={canEdit ? () => completeTask(it) : null}
                  onReopen={canEdit ? () => reopenTask(it) : null}
                />
              </SwipeRow>
            ))
          )}
        </div>
      </div>

      <Eyebrow>Заметки</Eyebrow>
      <div className="px-4">
        <div className="card-tg py-1 overflow-hidden">
          {!notes.length ? (
            <Empty text="Заметок нет" icon={Ion.chatbox} />
          ) : (
            notes.map((it, i) => (
              <SwipeRow key={it.id} onDelete={canEdit ? () => remove(it) : null} className={i > 0 ? "hairline-t" : ""}>
                <NoteItem n={it} />
              </SwipeRow>
            ))
          )}
        </div>
      </div>

      {/* Композер (ТЗ 6.5); в чтении его нет — писать в фид нечем */}
      {canEdit ? (
      <div className="px-4 pt-2">
        <div className="card-tg p-2.5">
          <div className="flex items-center gap-2 mb-2">
            <Seg value={kind} onChange={setKind} />
            {kind === "task" ? (
              <button
                className="ml-auto inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-semibold active:opacity-70"
                style={{
                  background: remind ? "var(--fin-gold)" : "color-mix(in srgb, var(--tg-hint) 12%, transparent)",
                  color: remind ? "#fff" : "var(--tg-hint)",
                }}
                onClick={() => { haptic.select(); setRemind((v) => !v); }}
              >
                <Ion.notifications size={12} />
                Напомнить
              </button>
            ) : null}
          </div>

          {kind === "task" ? (
            <div className="flex gap-2 mb-2">
              <input
                type="date"
                value={due}
                onChange={(e) => setDue(e.target.value)}
                className="num flex-1 rounded-lg px-2.5 py-2 text-[14px]"
                style={{ background: "var(--tg-secondary-bg)", color: "var(--tg-text)" }}
              />
              <input
                type="time"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
                className="num w-[92px] rounded-lg px-2.5 py-2 text-[14px]"
                style={{ background: "var(--tg-secondary-bg)", color: "var(--tg-text)" }}
              />
            </div>
          ) : null}

          <div className="flex items-end gap-2">
            <textarea
              rows={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={kind === "task" ? "Что нужно сделать" : "Заметка по карточке"}
              className="flex-1 resize-none rounded-lg px-3 py-2 text-[15px] leading-snug max-h-28"
              style={{ background: "var(--tg-secondary-bg)", color: "var(--tg-text)" }}
            />
            <button
              onClick={send}
              disabled={busy || !text.trim() || (kind === "task" && !due)}
              className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center active:opacity-70 disabled:opacity-35"
              style={{ background: "var(--tg-button)", color: "var(--tg-button-text)" }}
              aria-label="Отправить"
            >
              <Ion.paperPlane size={16} />
            </button>
          </div>

          {kind === "task" && !due ? (
            <div className="mt-1.5">
              <Hint>У задачи обязательны дата и время срока</Hint>
            </div>
          ) : null}
        </div>
      </div>
      ) : null}

    </>
  );
}

function Seg({ value, onChange }) {
  const opts = [
    { id: "note", label: "Заметка" },
    { id: "task", label: "Задача" },
  ];
  return (
    <div className="inline-flex rounded-lg p-0.5" style={{ background: "var(--tg-secondary-bg)" }}>
      {opts.map((o) => (
        <button
          key={o.id}
          onClick={() => { haptic.select(); onChange(o.id); }}
          className="rounded-md px-3 py-1 text-[13px] font-semibold"
          style={{
            background: value === o.id ? "var(--tg-section-bg)" : "transparent",
            color: value === o.id ? "var(--tg-text)" : "var(--tg-hint)",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function TaskItem({ t, today, onDone, onReopen }) {
  const overdue = !t.done && t.dueDate && toISO(t.dueDate) < today;
  const toggle = t.done ? onReopen : onDone;
  return (
    <div className="px-4 py-2.5 flex items-start gap-2.5">
      <button
        className="mt-0.5 active:opacity-60"
        onClick={toggle || undefined}
        disabled={!toggle}
        aria-label="Отметить"
      >
        {/* Готово — залитый кружок: контурный на 18px муарил в тёмное пятно */}
        {t.done ? (
          <Ion.checkmarkCircleSolid size={18} style={{ color: "var(--fin-green)" }} />
        ) : (
          <Ion.ellipse size={18} style={{ color: overdue ? "var(--fin-red)" : "var(--tg-hint)" }} />
        )}
      </button>

      <div className="min-w-0 flex-1">
        <div
          className="text-[14px] font-medium leading-snug"
          style={{ color: overdue ? "var(--fin-red)" : "var(--tg-text)", textDecoration: t.done ? "line-through" : "none", opacity: t.done ? 0.6 : 1 }}
        >
          {t.title}
        </div>
        <div className="mt-0.5 flex items-center gap-2 flex-wrap text-[12px]" style={{ color: "var(--tg-hint)" }}>
          <span>{t.who || "—"}</span>
          <span className="num">
            {t.dueDate ? ddmm(t.dueDate) : t.dueText || "без срока"}
            {t.dueTime ? ` ${t.dueTime}` : ""}
          </span>
          {overdue ? <span style={{ color: "var(--fin-red)" }}>просрочено</span> : null}
          {t.remind ? (
            <span className="inline-flex items-center gap-0.5" style={{ color: "var(--fin-gold)" }}>
              <Ion.notifications size={10} /> напоминание
            </span>
          ) : null}
        </div>

        {t.done && (t.result || t.comment) ? (
          <div
            className="mt-1.5 rounded-lg px-2.5 py-2 text-[13px]"
            style={{ background: "color-mix(in srgb, var(--fin-green) 8%, transparent)" }}
          >
            {t.result ? (
              <div>
                <span className="font-semibold" style={{ color: "var(--fin-green)" }}>Итог: </span>
                {t.result}
              </div>
            ) : null}
            {t.comment ? <div className="mt-0.5" style={{ color: "var(--tg-hint)" }}>{t.comment}</div> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function NoteItem({ n }) {
  return (
    <div className="px-4 py-2.5 flex items-start gap-2.5">
      <Ion.chatbox size={15} style={{ color: "var(--tg-hint)" }} className="mt-1 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[14px] leading-snug">
          <RichText>{n.text}</RichText>
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-[12px]" style={{ color: "var(--tg-hint)" }}>
          <span className="num">{ddmm(n.date || n.createdAt)}</span>
          {n.by ? <span>{n.by}</span> : null}
          {n.atts?.length ? (
            <span className="inline-flex items-center gap-0.5">
              <Ion.attach size={10} /> {n.atts.length}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
